/* ─── Solids Transition Guide Content ─────────────────────────────────────────
   Editorial content for the Knowledge tab "Your solids transition guide".
   Month-by-month guidance from 6M through 10–12M.
   Source: WHO Infant Feeding Guidelines · AAP PEDS · NHS Start4Life
────────────────────────────────────────────────────────────────────────────── */

export interface MonthGuide {
  id: string;
  label: string;              // "6M", "7M", "8–9M", "10–12M"
  theme: string;              // "Learn", "Widen", "Texture", "Family food"
  pill: string;               // short pill label shown on selected card
  about: string;              // what this month is about (1-2 sentences)
  detail: string;             // supporting paragraph
  foods: string[];            // food chips to show
  focus: string[];            // 4 bullet points
  milkPct: number;            // approximate milk % of nutrition
  solidsPct: number;          // approximate solids % of nutrition
  startMonth: number;         // inclusive calendar month offset from solids start
  endMonth: number;           // inclusive calendar month offset from solids start
  expectedMealsPerDay: number; // how many solid meals expected per day at this stage
  mealSlots: string[];        // meal slot names to show in plan card
}

export const MONTHS_GUIDE: MonthGuide[] = [
  {
    id: '6m',
    label: '6M',
    theme: 'Learn',
    pill: '1–2 small solid opportunities + milk',
    about: 'Milk remains primary. Solids are mostly about learning, tolerance, and gentle exposure.',
    detail: 'Parents should think less about volume and more about comfort, repetition, and safety. A teaspoon or two is a success at this stage.',
    foods: ['Banana', 'Apple puree', 'Moong dal mash', 'Rice cereal', 'Ragi porridge', 'Sweet potato'],
    focus: [
      'Introduce one simple food at a time',
      'Repeat foods 2–3 times before moving on',
      'Keep textures smooth and soft',
      'Watch comfort, stool changes, and any rash',
    ],
    milkPct: 80,
    solidsPct: 20,
    startMonth: 0,
    endMonth: 0,
    expectedMealsPerDay: 2,
    mealSlots: ['Lunch', 'Dinner'],
  },
  {
    id: '7m',
    label: '7M',
    theme: 'Widen',
    pill: '2 meals + milk',
    about: 'Shift from first exposure to stronger meal rhythm, more repeated foods, and broader category coverage.',
    detail: 'Baby has learned what eating is about. Now is the time to build variety — introducing new food groups while maintaining the foods they already accepted.',
    foods: ['Dal khichdi', 'Mashed potato', 'Pumpkin puree', 'Yogurt / curd', 'Ragi', 'Soft idli', 'Carrot puree'],
    focus: [
      'Repeat accepted foods — familiarity builds confidence',
      'Add 1 new food group per week (fruit, grain, veg, protein)',
      'Start a predictable lunch slot — same time daily',
      'Observe preferences — sweet vs savory, spoon vs mash',
    ],
    milkPct: 65,
    solidsPct: 35,
    startMonth: 1,
    endMonth: 1,
    expectedMealsPerDay: 2,
    mealSlots: ['Lunch', 'Dinner'],
  },
  {
    id: '8-9m',
    label: '8–9M',
    theme: 'Texture',
    pill: '3 meals + milk',
    about: 'Texture becomes the focus. Baby is ready for soft lumps, mashed foods, and first finger foods.',
    detail: 'Smooth purees are no longer the only option. Introducing gentle lumps now helps babies develop chewing skills and accept a wider variety of foods later.',
    foods: ['Soft idli', 'Paneer cubes', 'Soft boiled egg', 'Banana pieces', 'Steamed carrot sticks', 'Lentil soup', 'Oats porridge'],
    focus: [
      'Progress from smooth puree to mashed / soft lumpy textures',
      'Introduce soft finger foods to build self-feeding confidence',
      'Aim for 3 structured meals + 1 small snack per day',
      'Prioritise iron-rich foods at most meals',
    ],
    milkPct: 50,
    solidsPct: 50,
    startMonth: 2,
    endMonth: 3,
    expectedMealsPerDay: 3,
    mealSlots: ['Breakfast', 'Lunch', 'Dinner'],
  },
  {
    id: '10-12m',
    label: '10–12M',
    theme: 'Family food',
    pill: '3 meals + snacks, milk still important',
    about: 'Baby moves toward family foods with minimal modification. Less salt, less spice — same meal.',
    detail: 'Meals matter more now. Eating together as a family, allowing self-feeding, and reducing reliance on purees are the goals for this final phase before 12 months.',
    foods: ['Dal rice (family version)', 'Chapati pieces', 'Soft sabzi', 'Egg scramble', 'Fruit as snack', 'Curd rice', 'Upma'],
    focus: [
      'Eat together as a family where possible',
      'Reduce purees — prioritise family food adaptations',
      'Encourage self-feeding with spoon or fingers',
      "Cow's milk as main drink only after 12 months",
    ],
    milkPct: 40,
    solidsPct: 60,
    startMonth: 4,
    endMonth: 6,
    expectedMealsPerDay: 3,
    mealSlots: ['Breakfast', 'Lunch', 'Dinner'],
  },
];

export interface DayPlan {
  day: string;
  food: string;
  note: string;
}

export const WEEK1_PLAN: DayPlan[] = [
  { day: 'Day 1', food: 'Banana puree',       note: '1–2 spoons after morning milk' },
  { day: 'Day 2', food: 'Banana again',        note: 'Repeat before switching — builds tolerance' },
  { day: 'Day 3', food: 'Moong dal mash',      note: 'Small savory intro — a new flavour world' },
  { day: 'Day 4', food: 'Moong dal again',     note: 'Watch comfort, stool, and any skin changes' },
  { day: 'Day 5', food: 'Apple puree',         note: 'Simple fruit option — offer in daytime' },
  { day: 'Day 6', food: 'Apple again',         note: 'Repeat and observe how baby responds' },
  { day: 'Day 7', food: 'Rest / familiar food', note: 'Repeat an accepted food — no pressure day' },
];

/* Helper: pick the right month guide based on how many months since solids started */
export function getMonthGuideForSolidsAge(solidsStartDate: string | null | undefined): MonthGuide {
  if (!solidsStartDate) return MONTHS_GUIDE[0];
  const start = new Date(solidsStartDate);
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months = Math.max(0, months - 1);
  const guide = MONTHS_GUIDE.find(g => months >= g.startMonth && months <= g.endMonth);
  return guide ?? MONTHS_GUIDE[MONTHS_GUIDE.length - 1];
}
